import { LensOptimizer } from "./lens-optimizer.js";
import { webgl, stats, scaleDataForDisplay } from "./util.js";
import { vertexShaderSource, displayShaderSource } from "./shaders.js";
import {
  getInitialFieldState,
  getSimulationShaderSource,
} from "./simulation.js";

const DisplayMode = {
  FUNDAMENTAL: 0,
  SHG: 1,
  LENS: 2,
};

export class RenderSystem {
  constructor(config) {
    this.config = config.data;
    this.lensOptimizer = new LensOptimizer(this.config);
    this.isRunning = false;

    console.log("CONFIG", this.config);

    // GL contexts
    // mainGL for simulation AND primary display:
    const mainCanvas = document.getElementById("primaryCanvas");
    this.mainGL = mainCanvas.getContext("webgl2");
    if (!this.mainGL)
      throw new Error("WebGL 2 not available for primaryCanvas");

    const preview1Canvas = document.getElementById("preview1Canvas");
    this.preview1GL = preview1Canvas.getContext("webgl2");
    if (!this.preview1GL)
      throw new Error("WebGL 2 not available for preview1Canvas");

    const preview2Canvas = document.getElementById("preview2Canvas");
    this.preview2GL = preview2Canvas.getContext("webgl2");
    if (!this.preview2GL)
      throw new Error("WebGL 2 not available for preview2Canvas");

    [this.mainGL, this.preview1GL, this.preview2GL].forEach((gl) => {
      const ext = gl.getExtension("EXT_color_buffer_float");
      if (!ext) throw new Error("EXT_color_buffer_float not available");
    });

    // Programs
    // Simulation in mainGL
    this.programs = {
      simulation: null,
      primaryDisplay: null, // display in mainGL
      preview1Display: null, // display in preview1GL
      preview2Display: null, // display in preview2GL
    };

    // Simulation textures and framebuffers
    this.fundamentalTextures = [];
    this.fundamentalFramebuffers = [];
    this.shgTextures = [];
    this.shgFramebuffers = [];
    this.lensTexture = null;

    // Display textures (reused each frame)
    // For primary (mainGL), we can render directly from simulation textures—no extra texture needed.
    this.preview1Texture = null;
    this.preview2Texture = null;

    // Uniform locations
    this.uniformLocations = {
      simulation: {},
      primaryDisplay: {},
      preview1Display: {},
      preview2Display: {},
    };

    // Animation state
    this.frameCount = 0;
    this.current = 0;
    this.previous = 2;
    this.next = 1;

    // Readback buffers
    this.readbackBuffer = new Float32Array(
      this.config.gridSize * this.config.gridSize * 4,
    );
    this.shgDisplayBuffer = new Float32Array(
      this.config.gridSize * this.config.gridSize * 4,
    );
    this.lensDisplayBuffer = new Float32Array(
      this.config.gridSize * this.config.gridSize * 4,
    );

    // Lens stats
    this.lensStatistics = {
      baseIndex: { min: 1.0, max: 20.0 }, // typical refractive index range
      dispersion: { min: 0.0, max: 10.0 },
      chi2: { min: 0.0, max: 10.0 },
      chi3: { min: 0.0, max: 10.0 },
    };

    // Quad buffers
    this.quadBuffers = {
      main: null,
      preview1: null,
      preview2: null,
    };

    this.displayModes = {
      primary: DisplayMode.FUNDAMENTAL,
      preview1: DisplayMode.LENS,
      preview2: DisplayMode.SHG,
    };
  }

  async initialize() {
    try {
      this.initShaders();
      this.initBuffers();
      this.initTextures();
      console.log("Initialization complete");
      return true;
    } catch (error) {
      console.error("Initialization failed:", error);
      document.getElementById("statusDisplay").textContent =
        "Initialization failed: " + error.message;
      throw error;
    }
  }

  initShaders() {
    // Simulation
    this.programs.simulation = webgl.createProgram(
      this.mainGL,
      vertexShaderSource,
      getSimulationShaderSource(this.config),
    );

    // Display
    this.programs.primaryDisplay = webgl.createProgram(
      this.mainGL,
      vertexShaderSource,
      displayShaderSource,
    );

    this.programs.preview1Display = webgl.createProgram(
      this.preview1GL,
      vertexShaderSource,
      displayShaderSource,
    );

    this.programs.preview2Display = webgl.createProgram(
      this.preview2GL,
      vertexShaderSource,
      displayShaderSource,
    );

    this.cacheUniformLocations();
  }

  cacheUniformLocations() {
    // Simulation uniforms
    const simUniforms = [
      "u_dt",
      "u_dx",
      "u_damping",
      "u_c",
      "u_shg_Isat",
      "u_kerr_Isat",
      "u_chi",
      "u_chi2_ratio",
      "u_chi_ratio",
      "u_resolution",
      "u_boundaryR0",
      "u_boundaryAlpha",
      "u_boundaryM",
      "u_boundaryTransitionWidth",
      "u_updateTarget",
      "u_frameCount",
      "u_pulseInterval",
      "u_current",
      "u_previous",
      "u_lens",
      "u_fundamental",
      "u_shg",
      "u_lambdaFund",
      "u_lambdaSHG",
      "u_phaseRef",
      "u_gainMask",
      "u_gain0",
      "u_gainSat",
      "u_linearLoss",
      "u_crossKerrCoupling",
      "u_conversionCoupling",
      "u_beamWidth",
      "u_subsequentPulseAmplitude",
    ];
    this.mainGL.useProgram(this.programs.simulation);
    simUniforms.forEach((name) => {
      const loc = this.mainGL.getUniformLocation(
        this.programs.simulation,
        name,
      );
      this.uniformLocations.simulation[name] = loc;
    });

    const displayUniforms = [
      "u_field",
      "u_displayMode",
      "u_lensDisplayMin",
      "u_lensDisplayMax",
      "u_baseIndexRange",
      "u_chi2Range",
      "u_chi3Range",
      "u_lensRadius",
      "u_resolution",
    ];

    // Primary (mainGL)
    this.mainGL.useProgram(this.programs.primaryDisplay);
    displayUniforms.forEach((name) => {
      this.uniformLocations.primaryDisplay[name] =
        this.mainGL.getUniformLocation(this.programs.primaryDisplay, name);
    });

    // Preview1
    this.preview1GL.useProgram(this.programs.preview1Display);
    displayUniforms.forEach((name) => {
      this.uniformLocations.preview1Display[name] =
        this.preview1GL.getUniformLocation(this.programs.preview1Display, name);
    });

    // Preview2
    this.preview2GL.useProgram(this.programs.preview2Display);
    displayUniforms.forEach((name) => {
      this.uniformLocations.preview2Display[name] =
        this.preview2GL.getUniformLocation(this.programs.preview2Display, name);
    });
  }

  initBuffers() {
    this.quadBuffers.main = this.createQuadBuffer(this.mainGL);
    this.quadBuffers.preview1 = this.createQuadBuffer(this.preview1GL);
    this.quadBuffers.preview2 = this.createQuadBuffer(this.preview2GL);

    // Simulation attributes
    this.setupProgramAttributes(
      this.mainGL,
      this.programs.simulation,
      this.quadBuffers.main,
    );

    // Display attributes
    this.setupProgramAttributes(
      this.mainGL,
      this.programs.primaryDisplay,
      this.quadBuffers.main,
    );
    this.setupProgramAttributes(
      this.preview1GL,
      this.programs.preview1Display,
      this.quadBuffers.preview1,
    );
    this.setupProgramAttributes(
      this.preview2GL,
      this.programs.preview2Display,
      this.quadBuffers.preview2,
    );
  }

  createQuadBuffer(gl) {
    const quadBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW,
    );
    return quadBuffer;
  }

  setupProgramAttributes(gl, program, quadBuffer) {
    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
    const posLoc = gl.getAttribLocation(program, "position");
    if (posLoc >= 0) {
      gl.enableVertexAttribArray(posLoc);
      gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);
    }
  }

  initTextures() {
    // Simulation textures
    this.fundamentalTextures = [
      webgl.createTexture(
        this.mainGL,
        this.config.gridSize,
        this.config.gridSize,
      ),
      webgl.createTexture(
        this.mainGL,
        this.config.gridSize,
        this.config.gridSize,
      ),
      webgl.createTexture(
        this.mainGL,
        this.config.gridSize,
        this.config.gridSize,
      ),
    ];
    this.fundamentalFramebuffers = this.fundamentalTextures.map((t) =>
      webgl.createFramebuffer(this.mainGL, t),
    );

    this.shgTextures = [
      webgl.createTexture(
        this.mainGL,
        this.config.gridSize,
        this.config.gridSize,
      ),
      webgl.createTexture(
        this.mainGL,
        this.config.gridSize,
        this.config.gridSize,
      ),
      webgl.createTexture(
        this.mainGL,
        this.config.gridSize,
        this.config.gridSize,
      ),
    ];
    this.shgFramebuffers = this.shgTextures.map((t) =>
      webgl.createFramebuffer(this.mainGL, t),
    );

    this.lensTexture = webgl.createTexture(
      this.mainGL,
      this.config.gridSize,
      this.config.gridSize,
    );

    this.gainMaskTex = webgl.createTexture(
      this.mainGL,
      this.config.gridSize,
      this.config.gridSize,
    );

    // Display textures for previews
    // We'll allocate them once and reuse them by calling texSubImage2D each frame
    this.preview1Texture = webgl.createTexture(
      this.preview1GL,
      this.config.gridSize,
      this.config.gridSize,
    );
    this.preview2Texture = webgl.createTexture(
      this.preview2GL,
      this.config.gridSize,
      this.config.gridSize,
    );

    this.initializeFields();
  }

  initializeFields() {
    const { fundamentalData, shgData, gainMaskData, baseRMask } =
      getInitialFieldState(this.config);

    this.lensOptimizer.setBaseRMask(baseRMask);

    // Upload fundamental field textures
    this.fundamentalTextures.forEach((texture) => {
      this.mainGL.bindTexture(this.mainGL.TEXTURE_2D, texture);
      this.mainGL.texImage2D(
        this.mainGL.TEXTURE_2D,
        0,
        this.mainGL.RGBA32F,
        this.config.gridSize,
        this.config.gridSize,
        0,
        this.mainGL.RGBA,
        this.mainGL.FLOAT,
        fundamentalData,
      );
    });

    // Upload SHG field textures
    this.shgTextures.forEach((texture) => {
      this.mainGL.bindTexture(this.mainGL.TEXTURE_2D, texture);
      this.mainGL.texImage2D(
        this.mainGL.TEXTURE_2D,
        0,
        this.mainGL.RGBA32F,
        this.config.gridSize,
        this.config.gridSize,
        0,
        this.mainGL.RGBA,
        this.mainGL.FLOAT,
        shgData,
      );
    });

    // Upload initial gain mask (kept when adaptation is disabled)
    this.mainGL.bindTexture(this.mainGL.TEXTURE_2D, this.gainMaskTex);
    this.mainGL.texImage2D(
      this.mainGL.TEXTURE_2D,
      0,
      this.mainGL.RGBA32F,
      this.config.gridSize,
      this.config.gridSize,
      0,
      this.mainGL.RGBA,
      this.mainGL.FLOAT,
      gainMaskData,
    );

    // Seed a neutral lens texture immediately (n_o=n_e=1, axis=0, disp=0 from default modes)
    this.updateLensTexture();

    // Seed lens statistics once so previews have sane ranges even if adaptation is off
    this.updateLensStatistics();
  }

  readFieldData(texture) {
    const gl = this.mainGL;
    const fb = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      texture,
      0,
    );
    gl.readPixels(
      0,
      0,
      this.config.gridSize,
      this.config.gridSize,
      gl.RGBA,
      gl.FLOAT,
      this.readbackBuffer,
    );
    gl.deleteFramebuffer(fb);
    return this.readbackBuffer;
  }

  updateLensTexture() {
    const lensData = this.lensOptimizer.getLensData(
      this.config.gridSize,
      this.config.gridSize,
    );

    this.mainGL.bindTexture(this.mainGL.TEXTURE_2D, this.lensTexture);
    this.mainGL.texImage2D(
      this.mainGL.TEXTURE_2D,
      0,
      this.mainGL.RGBA32F,
      this.config.gridSize,
      this.config.gridSize,
      0,
      this.mainGL.RGBA,
      this.mainGL.FLOAT,
      lensData,
    );
  }

  updateLens() {
    // When adaptation is disabled, leave lens & gain-mask exactly as initialized.
    if (this.config.disableAdaptation) {
      return;
    }

    const fundamentalData = this.readFieldData(
      this.fundamentalTextures[this.current],
    );
    const shgData = this.readFieldData(this.shgTextures[this.current]);

    this.lensOptimizer.updateLens(
      fundamentalData,
      shgData,
      this.config.gridSize,
      this.config.gridSize,
    );

    // Refresh lens params texture (u_lens)
    this.updateLensTexture();

    // NOTE: CURRENTLY DISABLING THIS KEY BEHAVIOR. All the optimization stuff needs a second look basically
    // and hooking this up breaks the ability to look at other interesting/diverse configs, and optimization
    // isn't really do anything all that interesting even when this is hooked up anyway so..
    // Refresh gain mask texture (u_gainMask) from optimizer χ(2)/χ(3) + stored R mask

    if (this.config.enableGainAdaptation) {
      this.updateGainMaskTexture();
    }

    // Keep your stats display up to date
    this.updateLensStatistics();
  }

  updateGainMaskTexture() {
    const gl = this.mainGL;
    const size = this.config.gridSize;
    const gainMaskRGBA = this.lensOptimizer.getGainMaskData(size, size);

    gl.bindTexture(gl.TEXTURE_2D, this.gainMaskTex);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA32F,
      size,
      size,
      0,
      gl.RGBA,
      gl.FLOAT,
      gainMaskRGBA,
    );
  }

  updateLensStatistics() {
    const lensData = this.readFieldData(this.lensTexture); // R=n_o, G=n_e, B=axis, A=disp
    const gainData = this.readFieldData(this.gainMaskTex); // R=gain, G=chi2, B=chi3, A=1

    const baseValues = [];
    const dispersionValues = [];
    const chi2Values = [];
    const chi3Values = [];

    const cx = Math.floor(this.config.gridSize / 2);
    const cy = cx;

    for (let y = 0; y < this.config.gridSize; y++) {
      for (let x = 0; x < this.config.gridSize; x++) {
        const dx = x - cx,
          dy = y - cy;
        if (Math.hypot(dx, dy) <= this.config.lensRadius) {
          const i = (y * this.config.gridSize + x) * 4;
          baseValues.push(lensData[i + 0]); // n_o
          dispersionValues.push(lensData[i + 3]); // dispersion (A)
          chi2Values.push(gainData[i + 1]); // χ(2) weight (G)
          chi3Values.push(gainData[i + 2]); // χ(3) weight (B)
        }
      }
    }

    this.lensStatistics = {
      baseIndex: stats.calculateDisplayRange(baseValues),
      dispersion: stats.calculateDisplayRange(dispersionValues),
      chi2: stats.calculateDisplayRange(chi2Values),
      chi3: stats.calculateDisplayRange(chi3Values),
    };
  }

  render() {
    // Simulation step
    this.runSimulationStep();

    // Update lens occasionally
    if (
      this.frameCount % this.config.optimizationInterval === 0 &&
      this.frameCount > 10
    ) {
      this.updateLens();
    }

    // Render primary (no CPU readback if FUNDAMENTAL)
    this.renderPrimary();

    // Render previews (may involve CPU readback)
    this.renderPreview(
      this.preview1GL,
      this.uniformLocations.preview1Display,
      this.displayModes.preview1,
      this.preview1Texture,
      this.preview1CanvasSize(),
    );
    this.renderPreview(
      this.preview2GL,
      this.uniformLocations.preview2Display,
      this.displayModes.preview2,
      this.preview2Texture,
      this.preview2CanvasSize(),
    );

    // Cycle buffers
    [this.previous, this.current, this.next] = [
      this.current,
      this.next,
      this.previous,
    ];
    this.frameCount++;
  }

  runSimulationStep() {
    const gl = this.mainGL;
    gl.useProgram(this.programs.simulation);

    // Fundamental update
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fundamentalFramebuffers[this.next]);
    this.setSimulationUniforms(0);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    // SHG update
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.shgFramebuffers[this.next]);
    this.setSimulationUniforms(1);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  setSimulationUniforms(updateTarget) {
    const gl = this.mainGL;
    const uniforms = this.uniformLocations.simulation;

    // ---- Scalars & vectors (unchanged) ----
    gl.uniform1f(uniforms.u_dt, this.config.dt);
    gl.uniform1f(uniforms.u_dx, this.config.dx);
    gl.uniform1f(uniforms.u_damping, this.config.damping);
    gl.uniform1f(uniforms.u_c, this.config.c);
    gl.uniform1f(uniforms.u_chi_ratio, this.config.chi_ratio);
    gl.uniform1f(uniforms.u_chi2_ratio, this.config.chi2_ratio);
    gl.uniform1f(uniforms.u_shg_Isat, this.config.shg_Isat);
    gl.uniform1f(uniforms.u_kerr_Isat, this.config.kerr_Isat);
    gl.uniform1f(uniforms.u_chi, this.config.chi);

    gl.uniform2f(
      uniforms.u_resolution,
      this.config.gridSize,
      this.config.gridSize,
    );
    gl.uniform1f(uniforms.u_boundaryR0, this.config.boundaryR0);
    gl.uniform1f(uniforms.u_boundaryAlpha, this.config.boundaryAlpha);
    gl.uniform1f(uniforms.u_boundaryM, this.config.boundaryM);
    gl.uniform1f(
      uniforms.u_boundaryTransitionWidth,
      this.config.boundaryTransitionWidth,
    );

    gl.uniform1i(uniforms.u_updateTarget, updateTarget);
    gl.uniform1i(uniforms.u_pulseInterval, this.config.pulseInterval);
    gl.uniform1f(uniforms.u_beamWidth, this.config.beamWidth);
    gl.uniform1f(
      uniforms.u_subsequentPulseAmplitude,
      this.config.subsequentPulseAmplitude,
    );
    gl.uniform1i(uniforms.u_frameCount, this.frameCount + 1);

    gl.uniform1f(uniforms.u_lambdaFund, this.config.lambdaFund || 40.0);
    gl.uniform1f(uniforms.u_lambdaSHG, this.config.lambdaSHG || 20.0);
    gl.uniform1f(uniforms.u_phaseRef, this.config.phaseRef || 0.0);
    gl.uniform1f(uniforms.u_gain0, this.config.gain0 || 1.0);
    gl.uniform1f(uniforms.u_gainSat, this.config.gainSat || 1.0);
    gl.uniform1f(uniforms.u_linearLoss, this.config.linearLoss || 0.0);
    gl.uniform1f(
      uniforms.u_crossKerrCoupling,
      this.config.crossKerrCoupling || 0.0,
    );
    gl.uniform1f(
      uniforms.u_conversionCoupling,
      this.config.conversionCoupling || 0.0,
    );

    // ---- Band-local history (current / previous for the target band) ----
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(
      gl.TEXTURE_2D,
      updateTarget === 0
        ? this.fundamentalTextures[this.current]
        : this.shgTextures[this.current],
    );
    gl.uniform1i(uniforms.u_current, 0);

    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(
      gl.TEXTURE_2D,
      updateTarget === 0
        ? this.fundamentalTextures[this.previous]
        : this.shgTextures[this.previous],
    );
    gl.uniform1i(uniforms.u_previous, 1);

    // ---- Lens always needed ----
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.lensTexture);
    gl.uniform1i(uniforms.u_lens, 2);

    // ---- Always bind BOTH bands (phase mismatch, cross-Kerr, SHG walk-off) ----
    // Fundamental sampler: in the SHG pass, use just-updated fundamental (next); otherwise current.
    const fundForCross =
      updateTarget === 1
        ? this.fundamentalTextures[this.next]
        : this.fundamentalTextures[this.current];
    gl.activeTexture(gl.TEXTURE3);
    gl.bindTexture(gl.TEXTURE_2D, fundForCross);
    gl.uniform1i(uniforms.u_fundamental, 3);

    // SHG sampler: both passes need access to the current SHG
    gl.activeTexture(gl.TEXTURE4);
    gl.bindTexture(gl.TEXTURE_2D, this.shgTextures[this.current]);
    gl.uniform1i(uniforms.u_shg, 4);

    // ---- Gain mask (moved to unit 5) ----
    gl.activeTexture(gl.TEXTURE5);
    gl.bindTexture(gl.TEXTURE_2D, this.gainMaskTex);
    gl.uniform1i(uniforms.u_gainMask, 5);
  }

  renderPrimary() {
    const gl = this.mainGL;
    gl.useProgram(this.programs.primaryDisplay);

    const uniforms = this.uniformLocations.primaryDisplay;
    const mode = this.displayModes.primary;
    gl.uniform1i(uniforms.u_displayMode, mode);

    if (mode === 0) {
      // FUNDAMENTAL
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.fundamentalTextures[this.current]);
      gl.uniform1i(uniforms.u_field, 0);

      gl.uniform1f(uniforms.u_lensDisplayMin, 0.0);
      gl.uniform1f(uniforms.u_lensDisplayMax, 1.0);
      gl.uniform1f(uniforms.u_lensRadius, 0.0);
      gl.uniform2f(
        uniforms.u_resolution,
        this.config.gridSize,
        this.config.gridSize,
      );
    } else if (mode === 1) {
      // SHG
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.shgTextures[this.current]);
      gl.uniform1i(uniforms.u_field, 0);

      gl.uniform1f(uniforms.u_lensDisplayMin, 0.0);
      gl.uniform1f(uniforms.u_lensDisplayMax, 1.0);
      gl.uniform1f(uniforms.u_lensRadius, 0.0);
      gl.uniform2f(
        uniforms.u_resolution,
        this.config.gridSize,
        this.config.gridSize,
      );
    } else {
      // LENS
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.lensTexture);
      gl.uniform1i(uniforms.u_field, 0);

      // IMPORTANT: pass per-channel ranges
      gl.uniform1f(
        uniforms.u_lensDisplayMin,
        this.lensStatistics.dispersion.min,
      );
      gl.uniform1f(
        uniforms.u_lensDisplayMax,
        this.lensStatistics.dispersion.max,
      );

      gl.uniform2f(
        uniforms.u_baseIndexRange,
        this.lensStatistics.baseIndex.min,
        this.lensStatistics.baseIndex.max,
      );
      gl.uniform2f(
        uniforms.u_chi2Range,
        this.lensStatistics.chi2.min,
        this.lensStatistics.chi2.max,
      );
      gl.uniform2f(
        uniforms.u_chi3Range,
        this.lensStatistics.chi3.min,
        this.lensStatistics.chi3.max,
      );

      gl.uniform1f(uniforms.u_lensRadius, this.config.lensRadius);
      gl.uniform2f(
        uniforms.u_resolution,
        this.config.gridSize,
        this.config.gridSize,
      );
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  preview1CanvasSize() {
    return this.config.gridSize;
  }

  preview2CanvasSize() {
    return this.config.gridSize;
  }

  renderPreview(gl, uniforms, mode, texture, previewSize) {
    gl.useProgram(
      this.programs[
        gl === this.preview1GL ? "preview1Display" : "preview2Display"
      ],
    );

    let data = null;
    let lensSettings = { min: 0, max: 1, radius: 0 };
    const size = this.config.gridSize;

    if (mode === 0) {
      this.mainGL.bindFramebuffer(
        this.mainGL.FRAMEBUFFER,
        this.fundamentalFramebuffers[this.current],
      );
      this.mainGL.readPixels(
        0,
        0,
        size,
        size,
        this.mainGL.RGBA,
        this.mainGL.FLOAT,
        this.readbackBuffer,
      );
      scaleDataForDisplay(
        this.readbackBuffer,
        size,
        this.shgDisplayBuffer,
        previewSize,
      );
      data = this.shgDisplayBuffer;
    } else if (mode === 1) {
      this.mainGL.bindFramebuffer(
        this.mainGL.FRAMEBUFFER,
        this.shgFramebuffers[this.current],
      );
      this.mainGL.readPixels(
        0,
        0,
        size,
        size,
        this.mainGL.RGBA,
        this.mainGL.FLOAT,
        this.readbackBuffer,
      );
      scaleDataForDisplay(
        this.readbackBuffer,
        size,
        this.shgDisplayBuffer,
        previewSize,
      );
      data = this.shgDisplayBuffer;
    } else {
      const lensData = this.readFieldData(this.lensTexture); // raw RGBA: [n_o, n_e, axis, disp]
      // keep raw
      if (previewSize === size) {
        this.lensDisplayBuffer.set(lensData);
      } else {
        // nearest-neighbor fallback without normalization
        const scale = size / previewSize;
        let di = 0;
        for (let y = 0; y < previewSize; y++) {
          const sy = Math.min(size - 1, Math.floor(y * scale));
          for (let x = 0; x < previewSize; x++) {
            const sx = Math.min(size - 1, Math.floor(x * scale));
            const si = (sy * size + sx) * 4;
            this.lensDisplayBuffer[di++] = lensData[si + 0];
            this.lensDisplayBuffer[di++] = lensData[si + 1];
            this.lensDisplayBuffer[di++] = lensData[si + 2];
            this.lensDisplayBuffer[di++] = lensData[si + 3];
          }
        }
      }
      data = this.lensDisplayBuffer;
      lensSettings = {
        min: this.lensStatistics.dispersion.min,
        max: this.lensStatistics.dispersion.max,
        radius: this.config.lensRadius,
      };
    }

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texSubImage2D(
      gl.TEXTURE_2D,
      0,
      0,
      0,
      previewSize,
      previewSize,
      gl.RGBA,
      gl.FLOAT,
      data,
    );

    gl.uniform1i(uniforms.u_field, 0);
    gl.uniform1i(uniforms.u_displayMode, mode);
    gl.uniform1f(uniforms.u_lensDisplayMin, lensSettings.min);
    gl.uniform1f(uniforms.u_lensDisplayMax, lensSettings.max);
    gl.uniform1f(uniforms.u_lensRadius, lensSettings.radius);
    gl.uniform2f(uniforms.u_resolution, previewSize, previewSize);

    if (mode === 2) {
      gl.uniform2f(
        uniforms.u_baseIndexRange,
        this.lensStatistics.baseIndex.min,
        this.lensStatistics.baseIndex.max,
      );
      gl.uniform2f(
        uniforms.u_chi2Range,
        this.lensStatistics.chi2.min,
        this.lensStatistics.chi2.max,
      );
      gl.uniform2f(
        uniforms.u_chi3Range,
        this.lensStatistics.chi3.min,
        this.lensStatistics.chi3.max,
      );
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  setDisplayMode({ primary, preview1, preview2 }) {
    if (primary !== undefined) this.displayModes.primary = primary;
    if (preview1 !== undefined) this.displayModes.preview1 = preview1;
    if (preview2 !== undefined) this.displayModes.preview2 = preview2;
  }

  animate() {
    if (!this.isRunning) return;

    this.render();
    requestAnimationFrame(() => this.animate());
  }

  start() {
    this.isRunning = true;
    this.animate();
  }

  stop() {
    this.isRunning = false;
  }

  cleanup() {
    const glContexts = [
      {
        gl: this.mainGL,
        programs: [this.programs.simulation, this.programs.primaryDisplay],
        textures: [
          ...this.fundamentalTextures,
          ...this.shgTextures,
          this.lensTexture,
        ],
        buffers: [this.quadBuffers.main],
        framebuffers: [
          ...this.fundamentalFramebuffers,
          ...this.shgFramebuffers,
        ],
      },
      {
        gl: this.preview1GL,
        programs: [this.programs.preview1Display],
        textures: [this.preview1Texture],
        buffers: [this.quadBuffers.preview1],
      },
      {
        gl: this.preview2GL,
        programs: [this.programs.preview2Display],
        textures: [this.preview2Texture],
        buffers: [this.quadBuffers.preview2],
      },
    ];

    glContexts.forEach(
      ({ gl, programs, textures = [], buffers = [], framebuffers = [] }) => {
        if (!gl) return;

        // Delete programs
        programs.forEach((program) => {
          if (program) {
            // Detach and delete shaders first
            const attachedShaders = gl.getAttachedShaders(program);
            attachedShaders.forEach((shader) => {
              gl.detachShader(program, shader);
              gl.deleteShader(shader);
            });

            gl.deleteProgram(program);
          }
        });

        // Delete textures
        textures.forEach((texture) => {
          if (texture) gl.deleteTexture(texture);
        });

        // Delete buffers
        buffers.forEach((buffer) => {
          if (buffer) gl.deleteBuffer(buffer);
        });

        // Delete framebuffers
        framebuffers.forEach((framebuffer) => {
          if (framebuffer) gl.deleteFramebuffer(framebuffer);
        });

        // Unbind everything
        gl.bindBuffer(gl.ARRAY_BUFFER, null);
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
        gl.bindTexture(gl.TEXTURE_2D, null);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.bindRenderbuffer(gl.RENDERBUFFER, null);
        gl.useProgram(null);
      },
    );

    // Clear references
    this.programs = {};
    this.quadBuffers = {};
    this.fundamentalTextures = [];
    this.fundamentalFramebuffers = [];
    this.shgTextures = [];
    this.shgFramebuffers = [];
    this.lensTexture = null;
    this.preview1Texture = null;
    this.preview2Texture = null;

    // Clear WebGL context references
    this.mainGL = null;
    this.preview1GL = null;
    this.preview2GL = null;
  }
}
